import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FooterSystem from '../../components/system/FooterSystem';

interface GuideSection {
  id: string;
  title: string;
  content: React.ReactNode;
  subsections?: {
    id: string;
    title: string;
    content: React.ReactNode;
  }[];
}

export default function SystemUserGuide() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [activeSubsection, setActiveSubsection] = useState<string | null>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const guideSections: GuideSection[] = [
    {
      id: 'getting-started',
      title: 'Lesson 1: Getting Started',
      content: (
        <div className="space-y-4">
          <p>
            Welcome! This guide will help you understand how the system works and how to use all the tools at your disposal.
          </p>
        </div>
      ),
      subsections: [
        {
          id: 'overview',
          title: 'System Dashboard Overview',
          content: (
            <div className="space-y-4">
              <div className="lg:grid lg:grid-cols-2 lg:gap-6">
                <div className="prose max-w-none">
                  <p>The system dashboard provides you with:</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Today's performance metrics</li>
                    <li>Real-time race monitoring</li>
                    <li>Staking calculator for optimal bet sizing</li>
                    <li>Historical performance tracking</li>
                  </ul>
                </div>
                <div className="mt-4 lg:mt-0">
                  <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                    <video 
                      controls
                      className="absolute top-0 left-0 w-full h-full rounded-lg"
                      src="/videos/system/HRO%20System%20-%20Dashboard.mp4"
                      poster="/videos/thumbnails/lesson1.png"
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </div>
            </div>
          )
        },
        {
          id: 'daily-metrics',
          title: 'Understanding Daily Metrics',
          content: (
            <div className="space-y-4">
              <p>The daily metrics section shows:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Current Status: Shows if today's profit target has been achieved</li>
                <li>Bets Placed: Shows the number of bets you have placed today</li>
                <li>Profit/Loss: Shows the current profit/loss for the day</li>
              </ul>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      Metrics are updated in real-time based on the bets and results that you record in the system.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'races',
      title: "Lesson 2: Today's Races",
      content: (
        <div className="space-y-4">
          <p>
            Learn how to interpret the race information and use it effectively.
          </p>
        </div>
      ),
      subsections: [
        {
          id: 'race-view',
          title: 'Race View Features',
          content: (
            <div className="space-y-4">
              <div className="lg:grid lg:grid-cols-2 lg:gap-6">
                <div className="prose max-w-none">
                  <p>The race view displays:</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Upcoming races for today</li>
                    <li>Race time and venue</li>
                    <li>Runner details and odds</li>
                    <li>Which races you should bet on (qualifying races)</li>
                    <li>Confidence levels of qualifying favourites</li>
                  </ul>
                </div>
                <div className="mt-4 lg:mt-0">
                  <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                    <video 
                      controls
                      className="absolute top-0 left-0 w-full h-full rounded-lg"
                      src="/videos/system/HRO%20-%20Races.mp4"
                      poster="/videos/thumbnails/lesson2.png"
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'staking',
      title: 'Lesson 3: Staking Calculator',
      content: (
        <div className="space-y-4">
          <div className="lg:grid lg:grid-cols-2 lg:gap-6">
            <div className="prose max-w-none">
              <p>The staking calculator helps you:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Calculate optimal stake sizes based on your bank</li>
                <li>Adjust stakes based on on any losses your have incurred on the day</li>
                <li>Ensure the recommended stake will help you achieve your daily profit target</li>
              </ul>
            </div>
            <div className="mt-4 lg:mt-0">
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <video 
                  controls
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src="/videos/system/HRO%20-%20Staking%20Calculator.mp4"
                  poster="/videos/thumbnails/lesson3.png"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Always ensure that you have entered the correct balance and odds.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'recording-bets',
      title: 'Lesson 4: Recording Bets',
      content: (
        <div className="space-y-4">
          <div className="lg:grid lg:grid-cols-2 lg:gap-6">
            <div className="prose max-w-none">
              <p>Learn how to record your bets and manage your betting activity effectively:</p>
              <ol className="list-decimal list-inside space-y-2">
                <li>Identify a qualifying race from the Today's Races section</li>
                <li>Enter the runner name, the stake (as recommended by the staking calculator), and the odds for the favourite your are betting on (from the bookmaker of your choice)</li>
                <li>Click "Record Bet" to save the bet details</li>
                <li>The bet will appear in your betting history immediately</li>
                <li>Once the race is settled, you can update the result from 'pending' to 'won' or 'lost' in the betting history section</li>
              </ol>
            </div>
            <div className="mt-4 lg:mt-0">
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <video 
                  controls
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src="/videos/system/HRO%20-%20Recording%20Bets.mp4"
                  poster="/videos/thumbnails/lesson4.png"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      ),
      subsections: [
        {
          id: 'bet-management',
          title: 'Managing Your Bets',
          content: (
            <div className="space-y-4">
              <p>After recording a bet, you can:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>View bet details including stake, odds, and potential returns</li>
                <li>Update the bet status (Pending, Void, Won, Lost)</li>
                <li>Track the profit/loss once you record the result</li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      id: 'tracking-performance',
      title: 'Lesson 5: Tracking Performance',
      content: (
        <div className="space-y-4">
          <div className="lg:grid lg:grid-cols-2 lg:gap-6">
            <div className="prose max-w-none">
              <p>Monitor your betting performance and analyse your results over time:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>View cumulative profit/loss over time</li>
                <li>Analyze winning and losing streaks</li>
                <li>Track daily performance metrics</li>
                <li>Review complete betting history</li>
              </ul>
            </div>
            <div className="mt-4 lg:mt-0">
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <video 
                  controls
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src="/videos/system/HRO%20-%20Tracking%20Performance.mp4"
                  poster="/videos/thumbnails/lesson5.png"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      ),
      subsections: [
        {
          id: 'performance-chart',
          title: 'Performance Chart',
          content: (
            <div className="space-y-4">
              <p>The performance chart shows:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Cumulative profit/loss over time</li>
                <li>Visual representation of winning and losing streaks</li>
              </ul>
              <div className="bg-green-50 border-l-4 border-green-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      Use the performance chart to identify patterns and track your progress.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        },
        {
          id: 'betting-history',
          title: 'Betting History',
          content: (
            <div className="space-y-4">
              <p>The betting history provides:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Complete record of all bets placed</li>
                <li>Detailed statistics for each bet:
                  <ul className="list-none pl-5 pt-2 space-y-1">
                    <li>- Race details and time</li>
                    <li>- Horse name and odds</li>
                    <li>- Stake amount and result</li>
                    <li>- Profit/loss amount</li>
                  </ul>
                </li>
              </ul>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      Recording all your bets and results in the system will enable you to accurately track your betting history and progress.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-gray-900 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <img
                  src="/HRO-Logo-transparent-white-logo-white-text.png"
                  alt="HRO Logo"
                  className="h-10"
                />
              </div>
            </div>

            {/* Right side menu items */}
            <div className="hidden lg:ml-4 lg:flex lg:items-center lg:space-x-4">
              <Link
                to="/dashboard/system"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Dashboard
              </Link>
              <Link
                to="/dashboard/system/user-guide"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                User Guide
              </Link>
              <a
                href="/system/faqs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Support
              </a>
              <button
                onClick={handleSignOut}
                className="btn btn-secondary whitespace-nowrap"
              >
                Sign out
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#b29e84]"
              >
                <span className="sr-only">Open main menu</span>
                {/* Icon when menu is closed */}
                <svg
                  className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                {/* Icon when menu is open */}
                <svg
                  className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} lg:hidden bg-gray-900`}>
          <div className="border-t border-gray-800 pt-4 pb-3">
            <div className="space-y-1">
              <Link
                to="/dashboard/system"
                className="block px-4 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-800"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
              <Link
                to="/dashboard/system/user-guide"
                className="block px-4 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-800"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                User Guide
              </Link>
              <a
                href="/system/faqs"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-800"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Support
              </a>
              <button
                onClick={() => {
                  handleSignOut();
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-800"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-gray-900">
                System Dashboard Guide
              </h1>
            </div>

            <div className="grid grid-cols-12 gap-6">
              {/* Navigation Sidebar */}
              <nav className="col-span-12 lg:col-span-3">
                <div className="sticky top-6 space-y-4">
                  <div className="bg-white shadow rounded-lg p-6">
                    <div className="space-y-4">
                      {guideSections.map((section) => (
                        <div key={section.id} className="space-y-2">
                          <button
                            onClick={() => {
                              setActiveSection(section.id === activeSection ? null : section.id);
                              setActiveSubsection(null);
                              scrollToSection(section.id);
                            }}
                            className="text-left w-full flex items-center justify-between text-gray-900 hover:text-gray-600"
                          >
                            <span className="font-medium">{section.title}</span>
                            <svg
                              className={`h-5 w-5 transform transition-transform ${
                                activeSection === section.id ? 'rotate-180' : ''
                              }`}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                          {activeSection === section.id && section.subsections && (
                            <div className="pl-4 space-y-2">
                              {section.subsections.map((subsection) => (
                                <button
                                  key={subsection.id}
                                  onClick={() => {
                                    setActiveSubsection(subsection.id === activeSubsection ? null : subsection.id);
                                    scrollToSection(subsection.id);
                                  }}
                                  className={`text-left w-full text-sm ${
                                    activeSubsection === subsection.id
                                      ? 'text-primary-600 font-medium'
                                      : 'text-gray-900 hover:text-primary-600'
                                  }`}
                                >
                                  {subsection.title}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </nav>

              {/* Main Content */}
              <main className="col-span-12 lg:col-span-9 space-y-6">
                {guideSections.map((section) => (
                  <div
                    key={section.id}
                    ref={(el) => sectionRefs.current[section.id] = el}
                    className="bg-white shadow rounded-lg"
                  >
                    <div className="p-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                      {section.content}
                      
                      {section.subsections && (
                        <div className="mt-6 space-y-6">
                          {section.subsections.map((subsection) => (
                            <div 
                              key={subsection.id}
                              ref={(el) => sectionRefs.current[subsection.id] = el}
                              className="border-t border-gray-200 pt-6"
                            >
                              <h3 className="text-lg font-medium text-gray-900 mb-4">{subsection.title}</h3>
                              {subsection.content}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </main>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <FooterSystem />
    </div>
  );
} 